import { z } from 'zod';

// JS zod mirror of backend/src/contracts/supplierPublication.ts. Kept
// byte-for-byte faithful to the backend schemas so the FE parses every
// response through the same shape the backend serialises. Any drift is a
// contract break — the contract test round-trips the backend fixtures
// against these schemas to catch it.
//
// Authenticated CBAM Portal publication management contract
// (`GET|PUT /supplier/publication`, `POST /supplier/publication/slug/regenerate`).
// This is the supplier-facing settings surface, not the public payload, and may
// carry internal ids (`installationId`) for management UI display.

export const supplierPublicationObjectIdV1Schema = z.string().regex(/^[a-f\d]{24}$/i);

// `http:`/`https:` only — mirrors the backend's isHttpOrHttpsUrl refinement.
// A raw z.string().url() alone accepts `javascript:`/`data:`/`file:` etc,
// which is a stored-XSS foothold the moment websiteUrl/logoUrl renders as an
// href/src.
const HTTP_URL_PROTOCOLS = new Set(['http:', 'https:']);
export function isHttpOrHttpsUrl(value) {
  try {
    return HTTP_URL_PROTOCOLS.has(new URL(value).protocol);
  } catch {
    return false;
  }
}
const httpUrlV1Schema = z
  .string()
  .url()
  .refine(isHttpOrHttpsUrl, { message: 'URL must use the http or https scheme' });

const publicSlugV1Schema = z
  .string()
  .min(3)
  .max(80)
  .regex(
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    'Slug must be lowercase letters, numbers, and single hyphens',
  );

// Supplier brand accent (D50). Mirrors backend primaryColorV1Schema: accepts `#RRGGBB` in any
// case on the wire and normalizes to lower-case `#rrggbb`.
const primaryColorV1Schema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, 'primaryColor must be a #rrggbb hex color')
  .transform((value) => value.toLowerCase());

export const supplierPublicationLabelV1Schema = z
  .object({
    key: z.string().min(1).max(60),
    label: z.string().min(1).max(120),
    tone: z.enum(['info', 'success', 'warning', 'primary', 'neutral']),
  })
  .strict();

export const supplierPublicationCurrentSeriesV1Schema = z
  .object({
    seriesFingerprint: z.string().min(1),
    installationId: supplierPublicationObjectIdV1Schema,
    cnCode: z.string().min(1),
    productVariant: z.string(),
    revisionNumber: z.number().int().positive(),
    excluded: z.boolean(),
  })
  .strict();

export const supplierPublicationSettingsV1Schema = z
  .object({
    version: z.literal(1),
    slug: z.string().nullable(),
    passwordSet: z.boolean(),
    enabled: z.boolean(),
    includeFutureRevisions: z.boolean(),
    profileText: z.string().nullable(),
    websiteUrl: z.string().nullable(),
    logoUrl: z.string().nullable(),
    contactEmail: z.string().nullable(),
    primaryColor: z.string().nullable(),
    labels: z.array(supplierPublicationLabelV1Schema),
    viewCount: z.number().int().nonnegative(),
    lastAccessedAt: z.string().datetime().nullable(),
    currentSeries: z.array(supplierPublicationCurrentSeriesV1Schema),
  })
  .strict();

export const updateSupplierPublicationSettingsRequestV1Schema = z
  .object({
    version: z.literal(1),
    slug: publicSlugV1Schema.optional(),
    password: z.string().min(1).max(128).nullable().optional(),
    enabled: z.boolean(),
    includeFutureRevisions: z.boolean().optional(),
    excludedSeriesFingerprints: z.array(z.string().min(1)).optional(),
    profileText: z.string().max(2000).nullable().optional(),
    websiteUrl: httpUrlV1Schema.nullable().optional(),
    logoUrl: httpUrlV1Schema.nullable().optional(),
    contactEmail: z.string().email().nullable().optional(),
    primaryColor: primaryColorV1Schema.nullable().optional(),
    labels: z.array(supplierPublicationLabelV1Schema).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.enabled && value.includeFutureRevisions === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['includeFutureRevisions'],
        message: 'includeFutureRevisions is required to enable the CBAM Portal',
      });
    }
  });

export const regenerateSupplierPublicationSlugRequestV1Schema = z
  .object({
    version: z.literal(1),
  })
  .strict();
